import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Plus, Loader2, Search, Shield } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MarketplaceDisclaimer } from "@/components/MarketplaceDisclaimer";

export default function Marketplace() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "";
  const q = searchParams.get("q") || "";
  const [qLocal, setQLocal] = useState(q);
  const [categories, setCategories] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, isFirearmsPassActive, isAgeVerified } = useAuth();
  const navigate = useNavigate();

  const activeCat = categories.find((c) => c.id === category);
  const isFirearmView = !!activeCat?.firearm_class;
  const needsPass = isFirearmView && !isFirearmsPassActive;
  const needsVerify = isFirearmView && isFirearmsPassActive && !isAgeVerified;

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data.marketplace));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    api.get("/listings", { params }).then(({ data }) => {
      setListings(data);
      setLoading(false);
    });
  }, [category, q]);

  const onSearch = (e) => {
    e.preventDefault();
    const next = { ...(category ? { category } : {}) };
    if (qLocal) next.q = qLocal;
    setSearchParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="marketplace-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent mb-2">Marketplace</div>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">The Tackle Box</h1>
          <p className="text-muted-foreground mt-2">Buy, sell and trade gear with Florida sportsmen.</p>
        </div>
        <Button
          onClick={() => navigate(user ? "/listings/new" : "/login")}
          className="bg-secondary hover:bg-secondary/90"
          data-testid="btn-new-listing"
        >
          <Plus className="w-4 h-4 mr-2" /> List an item
        </Button>
      </div>

      <div className="mb-6">
        <MarketplaceDisclaimer />
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setSearchParams(q ? { q } : {})}
          className={`text-sm px-4 py-2 rounded-md border transition-colors duration-200 ${
            !category ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"
          }`}
          data-testid="mk-cat-all"
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setSearchParams({ category: c.id, ...(q ? { q } : {}) })}
            className={`text-sm px-4 py-2 rounded-md border transition-colors duration-200 inline-flex items-center gap-1 ${
              category === c.id
                ? c.firearm_class
                  ? "bg-secondary text-secondary-foreground border-secondary"
                  : "bg-primary text-primary-foreground border-primary"
                : c.firearm_class
                  ? "bg-card border-secondary/40 hover:border-secondary text-secondary"
                  : "bg-card border-border hover:border-primary/40"
            }`}
            data-testid={`mk-cat-${c.id}`}
          >
            {c.firearm_class && <Shield className="w-3 h-3" />}
            {c.name}
            {c.min_age && <span className="text-[10px] opacity-75">{c.min_age}+</span>}
          </button>
        ))}
      </div>

      <form onSubmit={onSearch} className="relative mb-8 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={qLocal}
          onChange={(e) => setQLocal(e.target.value)}
          placeholder="Search listings…"
          className="pl-9 bg-card"
          data-testid="input-search-listings"
        />
      </form>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-16 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading listings…
        </div>
      ) : listings.length === 0 ? (
        <Card className="bg-card">
          <CardContent className="py-16 text-center">
            <div className="font-display font-bold text-xl">No listings found.</div>
            <p className="text-muted-foreground mt-2">Be the first to sell some gear.</p>
            <Button className="mt-6 bg-secondary hover:bg-secondary/90" onClick={() => navigate(user ? "/listings/new" : "/login")} data-testid="btn-empty-new-listing">
              <Plus className="w-4 h-4 mr-2" /> List an item
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="listing-grid">
          {listings.map((l) => (
            <Link key={l.listing_id} to={`/listings/${l.listing_id}`} className="group" data-testid={`listing-card-${l.listing_id}`}>
              <Card className="overflow-hidden bg-card border-border transition-transform duration-300 ease-out group-hover:-translate-y-1 shadow-[0_4px_24px_rgba(27,77,62,0.06)]">
                <div className="relative aspect-[4/3] bg-muted overflow-hidden">
                  {l.image_paths?.[0] ? (
                    <img src={fileUrl(l.image_paths[0])} alt={l.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">No photo</div>
                  )}
                  {l.status === "sold" && (
                    <div className="absolute top-2 right-2">
                      <Badge className="bg-foreground text-background">SOLD</Badge>
                    </div>
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-baseline justify-between gap-2">
                    <h3 className="font-display font-bold text-base line-clamp-1 group-hover:text-primary transition-colors">{l.title}</h3>
                    <div className="font-display font-extrabold text-secondary shrink-0">${l.price.toFixed(0)}</div>
                  </div>
                  <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className="border-accent/40 text-accent bg-accent/5 text-[10px]">{l.condition}</Badge>
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {l.location}</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
